import { useEffect, useRef, useState } from "react";
import { Card, CardBody, Chip, Button } from "@heroui/react";
import { format } from "date-fns";
import { IconDownload } from "@tabler/icons-react";
import { api } from "@/lib/api";
import Loader from "@/components/loader";
import { useAuthStore } from "@/store/auth-store";
import { useStudioStore } from "@/store/studio-store";

function Empty({ text }: { text: string }) {
  return <div className="text-sm text-gray-400 text-center py-3">{text}</div>;
}

export default function WidgetPaymentsPanel({
  isActive,
}: {
  isActive: boolean;
}) {
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const wasActive = useRef(false);
  const authToken = useAuthStore((s) => s.token);
  const { studioId } = useStudioStore();

  useEffect(() => {
    if (!isActive) {
      wasActive.current = false;
      return;
    }

    if (wasActive.current) return;
    wasActive.current = true;

    const load = async () => {
      try {
        setLoading(true);
        const data = await api("/api/widget/profile/payments");
        setPayments(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isActive]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUCCESSFUL":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
      case "REFUNDED":
        return "danger";
      default:
        return "default";
    }
  };

  const downloadInvoice = async (paymentId: string) => {
    try {
      setDownloadingId(paymentId);

      const url = new URL(
        `/api/widget/invoices/${paymentId}/download`,
        window.location.origin,
      );
      if (studioId) url.searchParams.set("studioId", studioId);

      const headers: Record<string, string> = {};
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

      const response = await fetch(url.toString(), {
        credentials: "include",
        headers,
      });

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `invoice-${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <Loader />
      </div>
    );
  }

  if (payments.length === 0) {
    return <Empty text="No payments yet" />;
  }

  return (
    <div className="space-y-4">
      {payments.map((p) => {
        const rawDate = p.date || p.paidAt || p.createdAt;
        const date = rawDate ? new Date(rawDate) : null;
        const amount = Number(p.amount ?? 0);
        const currency = p.currency || "CHF";

        return (
          <Card key={p.id} className="p-4">
            <CardBody>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  {date && (
                    <div className="text-sm text-gray-500">
                      {format(date, "dd MMM yyyy")} · {format(date, "HH:mm")}
                    </div>
                  )}

                  <div className="font-medium mt-1">
                    {p.description || "Payment"}
                  </div>

                  {p.type && (
                    <div className="text-xs text-gray-500 mt-0.5">{p.type}</div>
                  )}
                </div>

                <div className="text-right">
                  <div className="font-semibold">
                    {amount.toFixed(2)} {currency}
                  </div>

                  <Chip
                    size="sm"
                    variant="flat"
                    color={getStatusColor(p.status)}
                    className="mt-2"
                  >
                    {p.status}
                  </Chip>
                </div>
              </div>

              {p.status === "SUCCESSFUL" && (
                <div className="mt-4 flex justify-end">
                  <Button
                    size="sm"
                    variant="light"
                    color="primary"
                    startContent={<IconDownload size={16} />}
                    isLoading={downloadingId === p.id}
                    onPress={() => downloadInvoice(p.id)}
                  >
                    Invoice
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}
